<!-- Filter input -->
@if (enableFiltering) {
  <div class="mb-3">
    <input type="text"
      [value]="globalFilter()"
      (input)="onFilterChange($event)"
      placeholder="Search..."
      aria-label="Filter table"
      class="w-full max-w-sm px-3 py-2 text-sm border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" />
  </div>
}

<!-- Virtual scroll wrapper -->
@if (enableVirtual) {
  <div #scrollContainer class="overflow-auto" [style.height]="virtualHeight" [style.max-height]="maxHeight || 'none'">
    <div [style.height.px]="virtualizer.getTotalSize()">
      <table [class]="'w-full border-collapse text-sm' + (enableStriping ? ' striped' : '')">
        <ng-container *ngTemplateOutlet="tableHead"></ng-container>
        <tbody>
          @for (virtualRow of virtualizer.getVirtualItems(); track virtualRow.index) {
            @if (getRowAtIndex(virtualRow.index); as row) {
              <!-- Group row -->
              @if (row.getIsGrouped()) {
                <tr class="bg-neutral-50 font-medium"
                  [style.height.px]="virtualRow.size"
                  [style.transform]="'translateY(' + (virtualRow.start - virtualRow.index * virtualRow.size) + 'px)'">
                  <td [attr.colspan]="table.getAllColumns().length + (enableSelection ? 1 : 0)" class="py-1.5 px-3 border-b border-neutral-100">
                    @if (enableGroupExpand) {
                      <button (click)="row.getToggleExpandedHandler()()" class="mr-2 text-neutral-500"
                        [attr.aria-label]="row.getIsExpanded() ? 'Collapse group' : 'Expand group'">
                        {{ row.getIsExpanded() ? '▼' : '▶' }}
                      </button>
                    }
                    <span>{{ row.groupingValue }}</span>
                  </td>
                </tr>
              } @else {
                <tr [class]="'border-b border-gray-100 hover:bg-gray-50/50 transition-colors ' + getRowClasses(row)"
                  [class.bg-primary-50]="row.getIsSelected()"
                  [style.height.px]="virtualRow.size"
                  [style.transform]="'translateY(' + (virtualRow.start - virtualRow.index * virtualRow.size) + 'px)'">
                  @if (enableSelection) {
                    <td class="py-1.5 px-2 border-b border-neutral-100 w-10 text-center">
                      <input type="checkbox"
                        [checked]="row.getIsSelected()"
                        (change)="row.getToggleSelectedHandler()($event)"
                        class="rounded border-neutral-300"
                        aria-label="Select row" />
                    </td>
                  }
                  @for (cell of row.getVisibleCells(); track cell.id) {
                    <td class="py-1.5 px-3 border-b border-neutral-100"
                      [class]="getCellClasses(row, cell.column.id)">
                      @if (cellTemplates[cell.column.id]) {
                        <ng-container [ngTemplateOutlet]="cellTemplates[cell.column.id]"
                          [ngTemplateOutletContext]="{ $implicit: cell.row.original, row: cell.row, cell: cell }">
                        </ng-container>
                      } @else {
                        <ng-container *flexRender="cell.column.columnDef.cell; props: cell.getContext(); let value">
                          {{ value }}
                        </ng-container>
                      }
                    </td>
                  }
                </tr>
              }
            }
          }
        </tbody>
        @if (enableFooter) {
          <ng-container *ngTemplateOutlet="tableFoot"></ng-container>
        }
      </table>
    </div>
  </div>
} @else {
  <div [style.max-height]="maxHeight || 'none'" [style.min-height]="maxHeight || 'none'" [class]="maxHeight ? 'overflow-auto' : ''">
    <table [class]="'w-full border-collapse text-sm' + (enableStriping ? ' striped' : '')">
      <ng-container *ngTemplateOutlet="tableHead"></ng-container>
      <tbody>
        @if (visibleRows().length === 0) {
          <tr>
            <td [attr.colspan]="table.getAllColumns().length + (enableSelection ? 1 : 0)" class="py-8 text-center text-neutral-400">
              <div>{{ emptyMessage }}</div>
              @if (emptyActionLabel && emptyActionRoute) {
                <a [routerLink]="emptyActionRoute" class="sz-btn-primary inline-block mt-3">{{ emptyActionLabel }}</a>
              }
            </td>
          </tr>
        }
        @for (row of visibleRows(); track row.id) {
          <!-- Group row -->
          @if (row.getIsGrouped()) {
            @if (!groupLabelFn || groupLabelFn(row.groupingValue) !== null) {
              <tr class="bg-neutral-50 font-medium">
                <td [attr.colspan]="table.getAllColumns().length + (enableSelection ? 1 : 0)" class="py-1.5 px-3 border-b border-neutral-100">
                  @if (enableGroupExpand) {
                    <button (click)="row.getToggleExpandedHandler()()" class="mr-2 text-neutral-500"
                      [attr.aria-label]="row.getIsExpanded() ? 'Collapse group' : 'Expand group'">
                      {{ row.getIsExpanded() ? '▼' : '▶' }}
                    </button>
                  }
                  <span>{{ groupLabelFn ? groupLabelFn(row.groupingValue) : row.groupingValue }}</span>
                </td>
              </tr>
            }
          } @else {
            <tr [class]="'border-b border-gray-100 hover:bg-gray-50/50 transition-colors ' + getRowClasses(row)"
              [class.bg-primary-50]="row.getIsSelected()">
              @if (enableSelection) {
                <td class="py-1.5 px-2 border-b border-neutral-100 w-10 text-center">
                  <input type="checkbox"
                    [checked]="row.getIsSelected()"
                    (change)="row.getToggleSelectedHandler()($event)"
                    class="rounded border-neutral-300" />
                </td>
              }
              @for (cell of row.getVisibleCells(); track cell.id) {
                <td class="py-1.5 px-3 border-b border-neutral-100"
                  [class]="getCellClasses(row, cell.column.id)">
                  @if (cellTemplates[cell.column.id]) {
                    <ng-container [ngTemplateOutlet]="cellTemplates[cell.column.id]"
                      [ngTemplateOutletContext]="{ $implicit: cell.row.original, row: cell.row, cell: cell }">
                    </ng-container>
                  } @else {
                    <ng-container *flexRender="cell.column.columnDef.cell; props: cell.getContext(); let value">
                      {{ value }}
                    </ng-container>
                  }
                </td>
              }
            </tr>
          }
        }
      </tbody>
      @if (enableFooter) {
        <ng-container *ngTemplateOutlet="tableFoot"></ng-container>
      }
    </table>
  </div>
  <!-- Pagination -->
  @if (enablePaging && pageCount > 1) {
    <div class="flex items-center justify-between px-3 py-2 border-t border-neutral-100 text-sm text-neutral-500">
      <span>
        Page {{ currentPage + 1 }} of {{ pageCount }}
      </span>
      <div class="flex gap-1">
        <button (click)="goToPage(0)" [disabled]="!table.getCanPreviousPage()"
          class="px-2 py-1 rounded border border-neutral-200 disabled:opacity-40 hover:bg-gray-50"
          aria-label="First page">
          &laquo;
        </button>
        <button (click)="previousPage()" [disabled]="!table.getCanPreviousPage()"
          class="px-2 py-1 rounded border border-neutral-200 disabled:opacity-40 hover:bg-gray-50"
          aria-label="Previous page">
          &lsaquo;
        </button>
        <button (click)="nextPage()" [disabled]="!table.getCanNextPage()"
          class="px-2 py-1 rounded border border-neutral-200 disabled:opacity-40 hover:bg-gray-50"
          aria-label="Next page">
          &rsaquo;
        </button>
        <button (click)="goToPage(pageCount - 1)" [disabled]="!table.getCanNextPage()"
          class="px-2 py-1 rounded border border-neutral-200 disabled:opacity-40 hover:bg-gray-50"
          aria-label="Last page">
          &raquo;
        </button>
      </div>
    </div>
  }
}

<!-- Standard (non-virtual) table -->

<!-- Shared table header -->
<ng-template #tableHead>
  <thead class="sticky top-0 z-10 bg-white">
    @for (headerGroup of table.getHeaderGroups(); track headerGroup.id; let first = $first) {
      <tr>
        @if (enableSelection && first) {
          <th class="py-2 px-2 text-center border-b border-neutral-200 bg-neutral-50 w-10"
            [attr.rowspan]="table.getHeaderGroups().length">
            @if (showSelectAll && selectionMode === 'multiple') {
              <input type="checkbox"
                [checked]="table.getIsAllRowsSelected()"
                [indeterminate]="table.getIsSomeRowsSelected() && !table.getIsAllRowsSelected()"
                (change)="table.getToggleAllRowsSelectedHandler()($event)"
                class="rounded border-neutral-300"
                aria-label="Select all rows" />
            }
          </th>
        }
        @for (header of headerGroup.headers; track header.id) {
          <th [attr.colspan]="header.colSpan"
            [style.width.px]="header.getSize() !== 150 ? header.getSize() : undefined"
            [class]="'py-2 px-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200 ' + (headerClassFn ? (headerClassFn(header.column.id) || 'bg-gray-50') : 'bg-gray-50')"
            [class.cursor-pointer]="header.column.getCanSort()"
            [class.select-none]="header.column.getCanSort()"
            (click)="header.column.getToggleSortingHandler()?.($event)">
            @if (!header.isPlaceholder) {
              <div class="flex items-center gap-1">
                <ng-container *flexRender="header.column.columnDef.header; props: header.getContext(); let headerText">
                  {{ headerText }}
                </ng-container>
                @if (header.column.getIsSorted() === 'asc') {
                  <span class="text-neutral-400">&#9650;</span>
                }
                @if (header.column.getIsSorted() === 'desc') {
                  <span class="text-neutral-400">&#9660;</span>
                }
              </div>
            }
          </th>
        }
      </tr>
    }
  </thead>
</ng-template>

<!-- Shared table footer -->
<ng-template #tableFoot>
  <tfoot class="sticky bottom-0 z-10">
    @for (footerGroup of table.getFooterGroups(); track footerGroup.id) {
      <tr class="bg-neutral-50 font-semibold border-t border-neutral-200">
        @if (enableSelection) {
          <td class="py-2 px-3 text-sm"></td>
        }
        @for (header of footerGroup.headers; track header.id) {
          <td class="py-2 px-3 text-sm"
            [attr.colspan]="header.colSpan">
            @if (!header.isPlaceholder) {
              <ng-container *flexRender="header.column.columnDef.footer; props: header.getContext(); let footerText">
                {{ footerText }}
              </ng-container>
            }
          </td>
        }
      </tr>
    }
  </tfoot>
</ng-template>
